import { DOCUMENT } from '@angular/common';
import { InjectionToken, inject } from '@angular/core';

/**
 * The browser's `location`, as a dependency in its own right.
 *
 * Several things here need to read the current host or hand the browser a new
 * URL, and each used to reach for `DOCUMENT` to get at `document.location`.
 * That states a far larger dependency than is really held -- and it is one a
 * spec cannot stub: Angular's own TestBed and platform internals inject
 * `DOCUMENT` too and call real DOM methods on it, so replacing it wholesale
 * breaks the framework before the component under test runs.
 *
 * This token names exactly what is used. Production gets it from the document;
 * a spec provides an object with the two or three properties that spec cares
 * about.
 *
 * The type is `Location | undefined` on purpose. Under SSR there is no browser
 * location worth the name, and every caller already guards with `?.` -- the
 * union keeps that guard honest instead of pushing a `!` onto every use.
 *
 * It lives beside `app-surface.ts` rather than in it because that file is
 * deliberately injector-free: `main.ts` imports it before
 * `bootstrapApplication` runs, where there is nothing to inject from.
 */
export const APP_LOCATION = new InjectionToken<Location | undefined>('APP_LOCATION', {
  providedIn: 'root',
  factory: () => inject(DOCUMENT).location,
});
